import { dedupeById } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import { useGeckoPermissions } from 'features/permissions'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { type MatchedDatapoint, useSiteDatapoints } from 'features/timeSeries'
import React, { createContext, useCallback, useContext, useMemo } from 'react'
import {
  PERMISSION_CANTILEVER_POWER_SUBJECTS_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
  type RolodexLoadedEntryJson,
} from 'types'

/** Raw unit data that may include linked datapoints (not typed on GeckoUnit) */
interface RawUnitData {
  name?: string
  linkedDatapoints?: string[]
  linkedPiTags?: string[]
}

type TimeSeriesUnitEntry = RolodexLoadedEntryJson & {
  entry: {
    data: RawUnitData
  }
}

interface TimeSeriesLinkContextType {
  /** Check if instrument text matches a unit with linked datapoints */
  hasTimeSeriesData: (instrumentText: string | undefined) => boolean
  /** Get matched datapoints for an instrument text */
  getMatchedDatapoints: (
    instrumentText: string | undefined
  ) => MatchedDatapoint[]
  /** Get the matched unit name (for display) */
  getMatchedInstrumentName: (
    instrumentText: string | undefined
  ) => string | null
  /** Resolve datapoint entry ids to datapoints; unknown ids are dropped */
  getDatapointsByIds: (ids: readonly string[]) => MatchedDatapoint[]
  isLoaded: boolean
}

interface TimeSeriesLinkProviderProps {
  children: React.ReactNode
  siteID: string
}

const TimeSeriesLinkContext = createContext<
  TimeSeriesLinkContextType | undefined
>(undefined)

/**
 * Normalize text for fuzzy matching:
 * - Remove hyphens, underscores, spaces
 * - Convert to uppercase
 */
export function normalizeForMatching(text: string): string {
  return text.replace(/[-_\s]/g, '').toUpperCase()
}

/**
 * Generate Element ↔ Transmitter variants for ISA instrument tags.
 * In ISA nomenclature, Elements (E) and Transmitters (T) often reference
 * the same physical measurement point:
 * - TE ↔ TT (Temperature)
 * - FE ↔ FT (Flow)
 * - PE ↔ PT (Pressure)
 * - LE ↔ LT (Level)
 * - AE ↔ AT (Analyzer)
 * etc.
 *
 * Returns the original text plus any E↔T variants.
 */
export function generateIsaVariants(text: string): string[] {
  const variants = [text]
  const upper = text.toUpperCase()

  // Pattern: matches instrument type codes like "TE", "TT", "FE", "FT", etc.
  // Looks for: start or separator, then letter + E or T, then separator or digit
  // Examples: "01-TE-BFW140" -> matches "TE"
  //           "TT-101" -> matches "TT"
  //           "01TE101" -> matches "TE"

  // Common ISA first letters: T(temp), F(flow), P(pressure), L(level), A(analyzer),
  // V(vibration), S(speed), Z(position), etc.
  const isaPattern = /([TFPLAZVSXW])([ET])(?=[-_\d]|$)/gi

  let match
  while ((match = isaPattern.exec(upper)) !== null) {
    const firstLetter = match[1]
    const secondLetter = match[2].toUpperCase()
    const swappedSecond = secondLetter === 'E' ? 'T' : 'E'

    // Create variant with swapped E↔T
    const variant =
      text.substring(0, match.index) +
      firstLetter +
      swappedSecond +
      text.substring(match.index + 2)

    variants.push(variant)
  }

  return variants
}

/**
 * Provider that queries Rolodex for units with instrumentation asset-type
 * and provides lookup functions to check if an instrument has linked datapoints.
 * Uses fuzzy text matching to handle variations in instrument names.
 */
export const TimeSeriesLinkProvider: React.FC<TimeSeriesLinkProviderProps> = ({
  children,
  siteID,
}) => {
  // Units are pipeline-config-agnostic; a config tag here matches nothing.
  // The notnull filter is load-bearing: a site can hold far more units than
  // any limit, and an unfiltered page truncates in unstable order.
  const unitLoadOptions = useMemo(
    () => ({
      type: RolodexEntryType.UNIT,
      tags: { site: siteID },
      filters: [{ field: 'data', key: 'linkedDatapoints', op: 'notnull' }],
      limit: 50000,
    }),
    [siteID]
  )
  const { data: unitEntries, isLoading } =
    useGetRolodexEntriesByLoadOptions<TimeSeriesUnitEntry[]>(unitLoadOptions)

  // Site datapoints, used to resolve subjects' tag-linked datapoint ids.
  // Tag-linked subjects only reach users who can see subjects at all
  // (useDocumentSubjects gates the fetch), so everyone else skips the load.
  const { hasPermissions } = useGeckoPermissions()
  const canSeeSubjects = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_CANTILEVER_POWER_SUBJECTS_VIEWER,
  ])
  const { datapointsById } = useSiteDatapoints(
    canSeeSubjects ? siteID : undefined
  )

  const getDatapointsByIds = useCallback(
    (ids: readonly string[]): MatchedDatapoint[] =>
      dedupeById(
        ids.flatMap((id) => {
          const datapoint = datapointsById.get(id)
          return datapoint ? [datapoint] : []
        })
      ),
    [datapointsById]
  )

  const { normalizedNameMap, exactNameMap, datapointsMap } = useMemo(() => {
    // Map normalized name -> original unit name
    const normalizedMap = new Map<string, string>()
    // Map exact uppercase name -> original unit name
    const exactMap = new Map<string, string>()
    // Map original unit name -> matched datapoints
    const dpMap = new Map<string, MatchedDatapoint[]>()

    // Filter to only units with linked datapoints (accessing raw data for untyped fields)
    const unitsWithDatapoints = (unitEntries ?? []).filter((unit) => {
      const rawData = unit.entry.data
      return (
        rawData.name &&
        rawData?.linkedDatapoints &&
        rawData.linkedDatapoints.length > 0
      )
    })

    for (const unit of unitsWithDatapoints) {
      const rawData = unit.entry.data
      const unitName = rawData.name
      const linkedDatapoints = rawData?.linkedDatapoints ?? []
      const linkedPiTags = rawData?.linkedPiTags ?? []

      // Skip units without a name (already filtered, but type safety)
      if (!unitName) continue

      const normalizedName = normalizeForMatching(unitName)
      const exactUpperName = unitName.toUpperCase()

      normalizedMap.set(normalizedName, unitName)
      exactMap.set(exactUpperName, unitName)

      // Build matched datapoints array from linked data
      const matchedDatapoints: MatchedDatapoint[] = []
      for (let i = 0; i < linkedDatapoints.length; i++) {
        const datapointId = linkedDatapoints[i]
        // Use corresponding PI tag if available, otherwise extract from ID
        const piTag =
          linkedPiTags[i] ??
          (datapointId.includes('-')
            ? datapointId.substring(datapointId.indexOf('-') + 1)
            : datapointId)

        matchedDatapoints.push({
          id: datapointId,
          pi_tag: piTag,
          name: piTag,
          description: piTag,
        })
      }

      dpMap.set(unitName, matchedDatapoints)
    }

    return {
      normalizedNameMap: normalizedMap,
      exactNameMap: exactMap,
      datapointsMap: dpMap,
    }
  }, [unitEntries])

  /**
   * Find the matching unit name for a given text.
   * Matching strategy (in order):
   * 1. Exact uppercase match
   * 2. Normalized fuzzy match (removes hyphens/underscores/spaces)
   * 3. ISA Element↔Transmitter variants (TE↔TT, FE↔FT, etc.)
   */
  const findMatchingInstrument = useMemo(() => {
    return (text: string | undefined): string | null => {
      if (!text) return null

      // Generate all variants including E↔T swaps
      const variants = generateIsaVariants(text)

      for (const variant of variants) {
        // Try exact uppercase match
        const exactUpper = variant.toUpperCase()
        if (exactNameMap.has(exactUpper)) {
          return exactNameMap.get(exactUpper) ?? null
        }

        // Try normalized fuzzy match (removes hyphens/underscores/spaces)
        const normalized = normalizeForMatching(variant)
        if (normalizedNameMap.has(normalized)) {
          return normalizedNameMap.get(normalized) ?? null
        }
      }

      return null
    }
  }, [exactNameMap, normalizedNameMap])

  const hasTimeSeriesData = useMemo(() => {
    return (instrumentText: string | undefined): boolean => {
      return findMatchingInstrument(instrumentText) !== null
    }
  }, [findMatchingInstrument])

  const getMatchedDatapoints = useMemo(() => {
    return (instrumentText: string | undefined): MatchedDatapoint[] => {
      const matchedName = findMatchingInstrument(instrumentText)
      if (!matchedName) return []
      return datapointsMap.get(matchedName) ?? []
    }
  }, [findMatchingInstrument, datapointsMap])

  const getMatchedInstrumentName = useMemo(() => {
    return (instrumentText: string | undefined): string | null => {
      return findMatchingInstrument(instrumentText)
    }
  }, [findMatchingInstrument])

  const contextValue: TimeSeriesLinkContextType = useMemo(
    () => ({
      hasTimeSeriesData,
      getMatchedDatapoints,
      getMatchedInstrumentName,
      getDatapointsByIds,
      isLoaded: unitEntries !== undefined && !isLoading,
    }),
    [
      hasTimeSeriesData,
      getMatchedDatapoints,
      getMatchedInstrumentName,
      getDatapointsByIds,
      isLoading,
      unitEntries,
    ]
  )

  return (
    <TimeSeriesLinkContext.Provider value={contextValue}>
      {children}
    </TimeSeriesLinkContext.Provider>
  )
}

export const useTimeSeriesLinkContext = (): TimeSeriesLinkContextType => {
  const context = useContext(TimeSeriesLinkContext)
  if (context === undefined) {
    throw new Error(
      'useTimeSeriesLinkContext must be used within a TimeSeriesLinkProvider'
    )
  }
  return context
}
